import { Modal, ConfirmModal, btn } from './AdminUI';
import React, { useEffect, useMemo, useState } from 'react';
import { Edit, Trash2, FileText, Download, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiCall, API_ENDPOINTS } from '../../config/api';
import { getCloudFrontUrl } from '../../utils/s3Upload';

/* A customer invoice is raised straight against a client master row, so it
   carries no AWB and never shows up beside a shipment. The API hands the row
   back flat, but an older shape nests it under `invoice` — read both. */
const asJson = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return null; }
};

export const readCustomerInvoice = (row) => {
  const inv = row?.invoice || row || {};
  const entries = (() => {
    const d = inv.invoiceData ?? inv.invoice_data ?? [];
    const parsed = asJson(d);
    return Array.isArray(parsed) ? parsed : [];
  })();
  const buyer = asJson(inv.buyer) || {};
  const summary = asJson(inv.summary) || {};
  return {
    id: row?.id ?? inv.id ?? null,
    number: entries[0]?.invoiceNumber || entries[0]?.invoice_number || '—',
    date: entries[0]?.invoiceDate || entries[0]?.invoice_date || null,
    buyerName: buyer.name || '—',
    buyerGstin: buyer.gstin || '',
    customerId: row?.customerId ?? row?.customer_id ?? buyer.customer_id ?? null,
    amount: Number(summary.grand_total ?? summary.grandTotal ?? 0) || 0,
    status: String(inv.status || 'unpaid').toLowerCase(),
    pdfKey: row?.invoicePdfKey || row?.invoice_pdf_key || inv.invoicePdfKey || inv.invoice_pdf_key || '',
    createdAt: row?.createdAt || row?.created_at || null,
  };
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB');
};

const formatMoney = (n) => `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0)}`;

const STATUS_STYLE = {
  paid:    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  unpaid:  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  partial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

/**
 * The invoices raised directly against a client — the ones with no shipment to
 * sit beside. Listed under Client Master for admin, super admin and accountant.
 */
const CustomerInvoices = ({ rows = [], search = '', onEdit, onDeleted }) => {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const list = useMemo(() => {
    const term = search.trim().toLowerCase();
    const parsed = rows.map((row) => ({ row, view: readCustomerInvoice(row) }));
    const sorted = parsed.sort((a, b) => {
      const ta = new Date(a.view.date || a.view.createdAt || 0).getTime() || 0;
      const tb = new Date(b.view.date || b.view.createdAt || 0).getTime() || 0;
      return tb - ta;
    });
    if (!term) return sorted;
    return sorted.filter(({ view }) => [view.number, view.buyerName, view.buyerGstin, String(view.amount)]
      .some((v) => String(v || '').toLowerCase().includes(term)));
  }, [rows, search]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const response = await apiCall(`${API_ENDPOINTS.INVOICE_DELETE}/${pendingDelete.view.id}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Invoice deleted');
        onDeleted?.(pendingDelete.view.id);
        setPendingDelete(null);
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.message || 'Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting customer invoice:', error);
      toast.error('Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden
                      dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                {[['Invoice No.'], ['Invoice Date'], ['Billed To'], ['GST No'], ['Amount', 'right'], ['Status'], ['Actions']]
                  .map(([h, align]) => (
                    <th key={h} className={`px-4 sm:px-5 py-3 text-[10.5px] font-black uppercase tracking-[0.1em]
                                            text-slate-400 dark:text-slate-500 whitespace-nowrap
                                            ${align === 'right' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
              {list.map(({ row, view }) => (
                <tr key={view.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition">
                  <td className="px-4 sm:px-5 py-3.5 whitespace-nowrap text-[13px] font-semibold text-slate-900 dark:text-white">{view.number}</td>
                  <td className="px-4 sm:px-5 py-3.5 whitespace-nowrap text-[13px] text-slate-500 dark:text-slate-400">{formatDate(view.date)}</td>
                  <td className="px-4 sm:px-5 py-3.5 text-[13px] text-slate-900 dark:text-white max-w-xs truncate" title={view.buyerName}>{view.buyerName}</td>
                  <td className="px-4 sm:px-5 py-3.5 whitespace-nowrap text-[13px] text-slate-500 dark:text-slate-400">{view.buyerGstin || '—'}</td>
                  <td className="px-4 sm:px-5 py-3.5 whitespace-nowrap text-[13px] text-right font-semibold text-slate-900 dark:text-white">{formatMoney(view.amount)}</td>
                  <td className="px-4 sm:px-5 py-3.5 whitespace-nowrap">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${STATUS_STYLE[view.status] || STATUS_STYLE.unpaid}`}>
                      {view.status}
                    </span>
                  </td>
                  <td className="px-4 sm:px-5 py-3.5 whitespace-nowrap text-[13px]">
                    <div className="flex gap-2">
                      {view.pdfKey && (
                        <a
                          href={getCloudFrontUrl(view.pdfKey)}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition"
                          title="Open invoice PDF"
                        >
                          <Download size={16} />
                        </a>
                      )}
                      <button
                        onClick={() => onEdit?.(row)}
                        className="p-1.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800 transition"
                        title="Edit Invoice"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setPendingDelete({ row, view })}
                        className="p-1.5 rounded bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800 transition"
                        title="Delete Invoice"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    <FileText size={28} className="mx-auto mb-3 opacity-40" />
                    {search ? 'No invoice matches this search' : 'No customer invoices yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={!!pendingDelete}
        busy={deleting}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete invoice"
        subtitle={pendingDelete ? `${pendingDelete.view.number} · ${pendingDelete.view.buyerName}` : undefined}
        confirmLabel="Delete invoice"
      >
        <p className="text-[13px] text-slate-600 dark:text-slate-300">
          This removes the invoice record and its number from the series. The PDF already sent to the
          client is not recalled.
        </p>
      </ConfirmModal>
    </>
  );
};

/**
 * The same list in a dialog, for the button that sits beside Generate Invoice —
 * the dashboards reach it without going through Client Master.
 */
export const CustomerInvoicesModal = ({ open, onClose, onEdit }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiCall(`${API_ENDPOINTS.INVOICE_SUBMIT}?invoice_type=customer`, { method: 'GET' });
        const data = res.ok ? await res.json() : null;
        if (cancelled) return;
        setRows(Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []));
      } catch (error) {
        if (!cancelled) toast.error('Failed to load customer invoices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-6xl my-8 shadow-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 sm:p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Customer Invoices</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Raised against a client, with no shipment attached — {rows.length} total
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice no, client, GST…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 text-slate-900 dark:text-white placeholder-gray-400"
            />
          </div>
          <button
            onClick={onClose}
            className="self-end sm:self-auto p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 sm:p-5">
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <CustomerInvoices
              rows={rows}
              search={search}
              onEdit={(row) => { onClose?.(); onEdit?.(row); }}
              onDeleted={(id) => setRows((prev) => prev.filter((r) => r.id !== id))}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerInvoices;
